import React, { useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MenuItem, CircularProgress } from '@mui/material';
import { mkConfig, generateCsv, download } from 'export-to-csv';

import { buildResistanceGeneRows } from '../AMRPredictions/resistanceGeneUtils.js';
import { useActiveAnalysis, useIdentifiedSpecies, useResistanceGenes } from '../services/api/KeynomeAPIHooks.js';
import { sortByIdConfidence } from './utilities.js';

const RESISTANCE_MARKER_CSV_HEADERS = [
    'dataset_name',
    'sample_name',
    'sample_type',
    'analysis_pipeline_version',
    'analysis_started_at',
    'analysis_completed_at',
    'organism',
    'drug_class',
    'resistance_marker',
    'gene_coverage',
    'unclassified_duplicate_marker',
];

export default function ExportResistanceMarkersMenuItem(props) {
    const {
        isIllumina,
        isDatasetLevel,
        run,
        samples,
        handleMenuClose,
        oneMenuItemClicked,
        setOneMenuItemClicked,
    } = props;
    const { setUserErrorMessage, setProgressMessage, hasAccessReportViruses } = useOutletContext();

    const { data: activeAnalyses, isLoading: analysesLoading } = useActiveAnalysis(samples?.map(sample => sample?.id));
    const activeAnalysisList = useMemo(() => {
        if (Array.isArray(activeAnalyses)) return activeAnalyses.filter(Boolean);
        return activeAnalyses ? [activeAnalyses] : [];
    }, [activeAnalyses]);
    const succeededAnalyses = useMemo(
        () => activeAnalysisList.filter(analysis => analysis?.attributes?.status === 'succeeded'),
        [activeAnalysisList]
    );
    const { data: identifiedSpecies, isLoading: identifiedSpeciesLoading } = useIdentifiedSpecies(
        succeededAnalyses.map(analysis => analysis?.id)
    );
    const exportableIdentifiedSpecies = useMemo(
        () => (identifiedSpecies || [])
            .filter(species => species?.attributes?.species_call_positive)
            .filter(species => (hasAccessReportViruses || !species.attributes.is_virus)),
        [identifiedSpecies, hasAccessReportViruses]
    );
    const { data: resistanceGenes, isLoading: resistanceGenesLoading } = useResistanceGenes(
        exportableIdentifiedSpecies.map(species => species?.id)
    );
    const dataLoading = analysesLoading || identifiedSpeciesLoading || resistanceGenesLoading;

    const resistanceMarkerRows = useMemo(() => {
        if (dataLoading) return [];

        return (samples || []).flatMap(sample => {
            const analysis = activeAnalysisList.find(_analysis => _analysis?.attributes?.sample_id === sample?.id);
            if (!analysis || analysis?.attributes?.status !== 'succeeded') return [];

            const sampleSpecies = sortByIdConfidence(
                exportableIdentifiedSpecies.filter(species => species?.attributes?.analyses_id === analysis?.id)
            );
            if (sampleSpecies.length === 0) return [];

            const sampleSpeciesIds = new Set(sampleSpecies.map(species => species.id));
            const sampleResistanceGenes = (resistanceGenes || [])
                .filter(gene => sampleSpeciesIds.has(gene?.attributes?.identified_species_id));
            const { rows } = buildResistanceGeneRows({
                identifiedSpecies: sampleSpecies,
                resistanceGenes: sampleResistanceGenes,
                isIllumina,
            });

            return rows.map(row => ({
                dataset_name: run?.attributes?.name,
                sample_name: sample?.attributes?.sample_name,
                sample_type: sample?.attributes?.sample_type,
                analysis_pipeline_version: analysis?.attributes?.metadata?.minion_pipeline_version,
                analysis_started_at: analysis?.attributes?.created_at,
                analysis_completed_at: analysis?.attributes?.updated_at,
                organism: row.organism,
                drug_class: row.drugClass,
                resistance_marker: row.gene,
                gene_coverage: row.coverage,
                unclassified_duplicate_marker: row.unclassifiedDuplicateMarker,
            }));
        });
    }, [dataLoading, samples, activeAnalysisList, exportableIdentifiedSpecies, resistanceGenes, isIllumina, run]);

    const handleExportClick = useCallback(async () => {
        setOneMenuItemClicked(true);
        setProgressMessage({ status: 'inProgress', message: 'Exporting AMR Markers report' });

        try {
            const csvConfig = mkConfig({
                columnHeaders: RESISTANCE_MARKER_CSV_HEADERS,
                filename: (isDatasetLevel ? run.attributes.name : samples[0].attributes.sample_name) + '_resistance_markers'
            });
            const csv = generateCsv(csvConfig)(resistanceMarkerRows);
            download(csvConfig)(csv);

            setProgressMessage({ status: 'completed', message: 'AMR Markers export completed.' });
        } catch (error) {
            setProgressMessage(null);
            setUserErrorMessage(
                'Failed to export AMR Markers! Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.'
            );
            throw new Error(`Request to export AMR Markers for ${samples?.map(sample => sample.attributes.sample_name)} in dataset ${run.attributes.name} failed: ${error}`);
        } finally {
            setOneMenuItemClicked(false);
        }
    }, [setOneMenuItemClicked, setProgressMessage, isDatasetLevel, run, samples, resistanceMarkerRows, setUserErrorMessage]);

    return <MenuItem disabled={oneMenuItemClicked || dataLoading} onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handleExportClick();
        handleMenuClose();
    }}>
        Export AMR Markers
        {dataLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
    </MenuItem>;
}
